class VariantSelectDropdown extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.activeIndex = -1;
    
    // Generate unique identifier for this instance
    this.instanceId = 'dropdown-' + Math.random().toString(36).substr(2, 9);
    
    // Bind methods to maintain context
    this.boundToggle = this.toggle.bind(this);
    this.boundOnClickOutside = this.onClickOutside.bind(this);
    this.boundOnKeyEvent = this.onKeyEvent.bind(this);
  }

  connectedCallback() {
    /** @type {HTMLButtonElement | null} */
    this.button = this.querySelector(".dropdown-button");
    /** @type {HTMLElement | null} */
    this.menu = this.querySelector(".dropdown-menu");
    /** @type {HTMLElement[]} */
    this.items = Array.from(this.querySelectorAll(".dropdown-menu-item"));
    
    if (this.button && this.menu && this.items.length > 1) {
      this.initEvents();
    }
  }

  disconnectedCallback() {
    // Clean up event listeners when component is removed
    if (this.button) {
      this.button.removeEventListener("click", this.boundToggle);
    }
    document.removeEventListener("click", this.boundOnClickOutside);
    document.removeEventListener("keydown", this.boundOnKeyEvent);
  }

  initEvents() {
    if (this.button) this.button.addEventListener("click", this.boundToggle);
    document.addEventListener("click", this.boundOnClickOutside);
    document.addEventListener("keydown", this.boundOnKeyEvent);
    
    // Add click events to menu items
    (this.items || []).forEach((item, index) => {
      item.addEventListener("click", (e) => {
        e.preventDefault();
        // Don't allow selection of unavailable variants
        if ((item.dataset || {}).variantAvailable !== 'false') {
          this.selectItem(index);
        }
      });
    });
  }

  toggle() {
    this.isOpen = !this.isOpen;
    this.button?.setAttribute("aria-expanded", this.isOpen.toString());
    this.menu?.setAttribute("aria-hidden", (!this.isOpen).toString());
    this.dataset.open = this.isOpen.toString();
    
    // Reset active index when opening
    if (this.isOpen) {
      this.activeIndex = -1;
    }
  }

  /** @param {number} index */
  selectItem(index) {
    // Handle item selection here
    const selectedItem = (this.items || [])[index];
    if (!selectedItem) return;
    const variantId = selectedItem.dataset?.variantId;
    const variantTitle = selectedItem.dataset?.variantTitle;
    const variantPrice = selectedItem.dataset?.variantPrice;
    const variantComparePrice = selectedItem.dataset?.variantComparePrice;
    const variantAvailable = selectedItem.dataset?.variantAvailable === 'true';
    const variantPoints = selectedItem.dataset?.variantPoints;
    
    /** @type {HTMLInputElement | null} */
    const inputVariantId = /** @type {HTMLInputElement | null} */ (this.nextElementSibling?.querySelector('input[name="id"]'));

    const inputVariantInventoryLevels = /** @type {HTMLInputElement | null} */ (this.nextElementSibling?.querySelector('input[name="properties[_variant_inventory_levels]"]'));

    if (inputVariantId) {
      inputVariantId.value = variantId || '';
    }

    if (inputVariantInventoryLevels) {
      inputVariantInventoryLevels.value = selectedItem.dataset?.variantInventoryLevels || '';
    }

    // Update button text with selected variant
    const buttonText = this.button ? this.button.querySelector('.variant-title') : null;
    if (buttonText && variantTitle) {
      buttonText.textContent = `${variantTitle} / `;
    }

    const priceTextElement = this.button ? this.button.querySelector('.variant-price') : null;
    if(priceTextElement && variantPrice) {
      // Get the price from the actual price display in the menu item
      const priceElement = selectedItem.querySelector('.variant-price');
      const priceText = priceElement && priceElement.textContent ? priceElement.textContent.trim() : '';
      priceTextElement.textContent = `${priceText}`;
    }

    const compareAtPriceTextElement = this.button ? this.button.querySelector('.variant-compare-price') : null;
    if(compareAtPriceTextElement && variantComparePrice) {
      const compareAtPriceElement = selectedItem.querySelector('.variant-compare-price');
      const compareAtPriceText = compareAtPriceElement && compareAtPriceElement.textContent ? compareAtPriceElement.textContent.trim() : '';
      compareAtPriceTextElement.textContent = `${compareAtPriceText}`;
    }
    
    if(variantPoints && variantPoints != '') {
      const pointsTextElement = document.querySelector('.product-points-number');
      if(pointsTextElement) {
        pointsTextElement.textContent = `${parseInt(variantPoints)}`;
      }
    }

    // Update selected state
    (this.items || []).forEach(item => item.removeAttribute('data-selected'));
    selectedItem.setAttribute('data-selected', 'true');
    
    // Close dropdown
    this.isOpen = false;
    if (this.button) this.button.setAttribute("aria-expanded", "false");
    if (this.menu) this.menu.setAttribute("aria-hidden", "true");
    this.dataset.open = "false";
    
    // Focus back to button
    if (this.button && typeof this.button.focus === 'function') this.button.focus();
    
    // Dispatch custom event for variant selection
    this.dispatchEvent(new CustomEvent('variant-selected', {
      detail: { 
        instanceId: this.instanceId,
        variantId: variantId,
        variantTitle: variantTitle,
        variantPrice: variantPrice,
        variantComparePrice: variantComparePrice,
        variantAvailable: variantAvailable,
        selectedIndex: index,
        selectedElement: selectedItem
      },
      bubbles: true
    }));
    
    // Also dispatch a change event for compatibility with existing variant picker logic
    this.dispatchEvent(new CustomEvent('change', {
      detail: {
        instanceId: this.instanceId,
        variantId: variantId
      },
      bubbles: true
    }));
  }

  /** @param {MouseEvent} e */
  onClickOutside(e) {
    if (!this.isOpen) return;

    /** @type {Node | null} */
    let targetElement = e.target instanceof Node ? e.target : null;

    while (targetElement != null) {
      if (targetElement === this) return;
      targetElement = targetElement.parentNode;
    }

    this.toggle();
  }

  /** @param {KeyboardEvent} e */
  onKeyEvent(e) {
    // Only handle keyboard events if this specific dropdown is open
    if (!this.isOpen || !this.button || !(this.items && this.items.length)) return;

    // Prevent default behavior for arrow keys to avoid page scrolling
    if (["ArrowDown", "ArrowUp", "Enter", "Space"].includes(e.key)) {
      e.preventDefault();
    }

    if (e.key === "Tab" || e.key === "Escape") {
      this.toggle();
      this.button.focus();
      return;
    }

    if (e.key === "ArrowDown") {
      this.moveToNextAvailableItem(1);
    }

    if (e.key === "ArrowUp") {
      this.moveToNextAvailableItem(-1);
    }

    if (e.key === "Enter" || e.key === " ") {
      if (this.activeIndex >= 0 && this.activeIndex < (this.items || []).length) {
        const activeItem = (this.items || [])[this.activeIndex];
        // Only select if variant is available
        if (activeItem && (activeItem.dataset || {}).variantAvailable !== 'false') {
          this.selectItem(this.activeIndex);
        }
      }
    }
  }

  /** @param {number} direction */
  moveToNextAvailableItem(direction) {
    const totalItems = (this.items || []).length;
    let newIndex = this.activeIndex;
    let attempts = 0;
    
    do {
      newIndex = direction > 0 
        ? (newIndex < totalItems - 1 ? newIndex + 1 : 0)
        : (newIndex > 0 ? newIndex - 1 : totalItems - 1);
      attempts++;
    } while (
      attempts < totalItems && 
      (this.items || [])[newIndex]?.dataset?.variantAvailable === 'false'
    );
    
    // If we found an available item or went through all items
    if (attempts < totalItems || (this.items || [])[newIndex]?.dataset?.variantAvailable !== 'false') {
      this.activeIndex = newIndex;
      this.focusActiveItem();
    }
  }

  focusActiveItem() {
    // Remove focus from all items
    (this.items || []).forEach(item => item.setAttribute('tabindex', '-1'));
    
    // Focus the active item
    if (this.activeIndex >= 0 && this.activeIndex < (this.items || []).length) {
      const activeItem = (this.items || [])[this.activeIndex];
      if (activeItem) {
        activeItem.setAttribute('tabindex', '0');
        if (typeof activeItem.focus === 'function') activeItem.focus();
      }
    }
  }
}

customElements.define('variant-select-dropdown', VariantSelectDropdown);